import { useEffect, useState, useCallback } from 'react';
import { Plus, Trash2, Loader2, RefreshCw, MessageCircle } from 'lucide-react';
import { SuperAdminLayout } from '@/components/super-admin/SuperAdminLayout';
import { saApi } from '@/contexts/SuperAdminAuthContext';
import { toast } from '@/components/ui/toast';
import { SelectField } from '@/components/ui/SelectField';

// ── Super-admin WhatsApp Templates ─────────────────────────────────────────
// Manages message templates on the PLATFORM WhatsApp account (the one connected
// under Settings → Integrations → WhatsApp Business). Needed so alerts to the
// assigned admin (new lead / demo booked) can send via WhatsApp — Meta requires
// an approved template for any business-initiated message.

interface WaAccount { id: string; provider?: string; displayPhoneNumber: string; status: string }
interface WaTemplate { id: string; name: string; language: string; category: string; status: string; rejectionReason?: string | null }

const CATEGORIES = ['UTILITY', 'MARKETING', 'AUTHENTICATION'];

// One-click, Meta-compliant templates for the alert flows. Submit for approval;
// once APPROVED the matching alert (new lead / demo booked) sends via WhatsApp.
const READY_TEMPLATES = [
  { name: 'new_lead_alert', label: 'New lead alert', category: 'UTILITY',
    body: '🔔 New lead assigned to you: {{1}} · {{2}} — interested in {{3}}. Open BrixLead to follow up.' },
  { name: 'demo_booking_alert', label: 'Demo booked alert', category: 'UTILITY',
    body: '📅 New demo booked with you: {{1}} on {{2}}. Open BrixLead to prepare.' },
];
const STATUS_STYLE: Record<string, string> = {
  APPROVED: 'bg-emerald-50 text-emerald-700',
  PENDING: 'bg-amber-50 text-amber-700',
  REJECTED: 'bg-red-50 text-red-600',
  PAUSED: 'bg-orange-50 text-orange-700',
  DISABLED: 'bg-gray-100 text-gray-500',
  DELETED: 'bg-gray-100 text-gray-400',
};

const inputCls = 'w-full h-10 px-3 rounded-lg border border-gray-200 bg-white text-sm text-[#1E293B] focus:outline-none focus:ring-2 focus:ring-[#6366F1] focus:border-transparent placeholder:text-[#94A3B8]';

export default function SuperAdminWhatsAppTemplatesPage() {
  const [account, setAccount] = useState<WaAccount | null>(null);
  const [noAccount, setNoAccount] = useState(false);
  const [templates, setTemplates] = useState<WaTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [creating, setCreating] = useState(false);
  const [showForm, setShowForm] = useState(false);

  // Create form
  const [name, setName] = useState('');
  const [language, setLanguage] = useState('en_US');
  const [category, setCategory] = useState('UTILITY');
  const [body, setBody] = useState('');

  const loadAccounts = useCallback(async () => {
    try {
      const { data } = await saApi.get('/super-admin/whatsapp/accounts');
      const list: WaAccount[] = Array.isArray(data) ? data : [];
      const connected = list.find(a => a.status === 'CONNECTED') || null;
      setAccount(connected);
      setNoAccount(!connected);
    } catch { setNoAccount(true); }
  }, []);

  const loadTemplates = useCallback(async (accId?: string) => {
    if (!accId) { setTemplates([]); setLoading(false); return; }
    setLoading(true);
    try {
      const { data } = await saApi.get('/super-admin/whatsapp/templates', { params: { whatsappAccountId: accId, limit: 100 } });
      setTemplates(Array.isArray(data?.data) ? data.data : []);
    } catch { setTemplates([]); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { loadAccounts(); }, [loadAccounts]);
  useEffect(() => { if (account?.id) loadTemplates(account.id); else setLoading(false); }, [account?.id, loadTemplates]);

  const buildComponents = (text: string) => {
    const varCount = (text.match(/\{\{(\d+)\}\}/g) || []).length;
    const comp: any = { type: 'BODY', text };
    if (varCount > 0) {
      comp.example = { body_text: [Array.from({ length: varCount }, (_, i) => `Example ${i + 1}`)] };
    }
    return [comp];
  };

  const createTemplate = async (payload: { name: string; language: string; category: string; body: string }) => {
    if (!account?.id) { toast('error', 'Connect the platform WhatsApp first (Settings → Integrations).'); return; }
    if (!payload.name.trim() || !payload.body.trim()) { toast('error', 'Name and body are required'); return; }
    setCreating(true);
    try {
      await saApi.post('/super-admin/whatsapp/templates', {
        whatsappAccountId: account.id,
        name: payload.name.trim().toLowerCase().replace(/[^a-z0-9_]/g, '_'),
        language: payload.language.trim() || 'en_US',
        category: payload.category,
        components: buildComponents(payload.body.trim()),
      });
      toast('success', 'Template submitted for approval');
      setShowForm(false); setName(''); setBody('');
      loadTemplates(account.id);
    } catch (e: any) {
      toast('error', e?.response?.data?.message || 'Could not create template');
    } finally { setCreating(false); }
  };

  const createReady = (t: typeof READY_TEMPLATES[number]) =>
    createTemplate({ name: t.name, language: 'en_US', category: t.category, body: t.body });

  const sync = async () => {
    if (!account?.id) return;
    setSyncing(true);
    try {
      await saApi.post('/super-admin/whatsapp/templates/sync', { whatsappAccountId: account.id });
      toast('success', 'Sync started — refreshing shortly');
      setTimeout(() => loadTemplates(account.id), 3000);
    } catch { toast('error', 'Sync failed'); }
    finally { setSyncing(false); }
  };

  const remove = async (id: string) => {
    if (!confirm('Delete this template?')) return;
    try {
      await saApi.delete(`/super-admin/whatsapp/templates/${id}`);
      toast('success', 'Template deleted');
      loadTemplates(account?.id);
    } catch { toast('error', 'Delete failed'); }
  };

  const statusOf = (name: string) => templates.find(t => t.name === name)?.status;

  return (
    <SuperAdminLayout>
      <div className="mx-auto max-w-4xl px-4 py-6 space-y-5">
        {/* Header */}
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="text-xl font-semibold text-[#1E293B] flex items-center gap-2">
              <MessageCircle className="h-5 w-5 text-[#25D366]" /> WhatsApp Templates
            </h1>
            <p className="text-sm text-[#64748B]">
              Templates on the platform WhatsApp — used for lead / demo alerts to the assigned admin.
              {account && <span className="text-[#94A3B8]"> · {account.displayPhoneNumber}</span>}
            </p>
          </div>
          {account && (
            <div className="flex items-center gap-2">
              <button onClick={sync} disabled={syncing}
                className="inline-flex items-center gap-1.5 rounded-md border border-gray-200 px-3 py-2 text-sm text-slate-600 hover:bg-slate-50 disabled:opacity-50">
                {syncing ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />} Sync
              </button>
              <button onClick={() => setShowForm(v => !v)}
                className="inline-flex items-center gap-1.5 rounded-md bg-[#6366F1] px-3 py-2 text-sm font-medium text-white hover:bg-[#4F46E5]">
                <Plus className="h-4 w-4" /> New template
              </button>
            </div>
          )}
        </div>

        {/* No platform WhatsApp connected */}
        {noAccount && (
          <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
            No platform WhatsApp connected yet. Connect one under <b>Settings → Integrations → WhatsApp Business</b>, then come back here.
          </div>
        )}

        {/* Ready-made alert templates — one click to submit for approval */}
        {account && (
          <div className="rounded-xl border border-indigo-100 bg-indigo-50/50 px-4 py-3.5 space-y-2.5">
            <p className="text-sm font-semibold text-[#334155]">Ready-made alert templates</p>
            <p className="text-[12px] text-[#64748B] -mt-1">Create these + get them approved by Meta so the assigned admin gets a WhatsApp when a new lead comes in / a demo is booked.</p>
            {READY_TEMPLATES.map(t => {
              const st = statusOf(t.name);
              return (
                <div key={t.name} className="flex items-center justify-between gap-3 rounded-lg bg-white border border-indigo-100 px-3 py-2.5">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-medium text-[#1E293B]">{t.label}</span>
                      <span className="font-mono text-[11px] text-[#94A3B8]">{t.name}</span>
                      {st && <span className={`text-[11px] px-2 py-0.5 rounded-full font-medium ${STATUS_STYLE[st] ?? 'bg-slate-100 text-slate-600'}`}>{st}</span>}
                    </div>
                    <p className="text-[11px] text-[#94A3B8] mt-0.5 truncate">{t.body}</p>
                  </div>
                  {st === 'APPROVED' ? (
                    <span className="text-[12px] text-emerald-600 font-semibold flex-shrink-0">✓ Ready</span>
                  ) : st ? (
                    <span className="text-[12px] text-amber-600 font-medium flex-shrink-0">Submitted</span>
                  ) : (
                    <button onClick={() => createReady(t)} disabled={creating}
                      className="inline-flex items-center gap-1.5 rounded-md bg-[#6366F1] px-3 py-1.5 text-[13px] font-medium text-white hover:bg-[#4F46E5] disabled:opacity-50 flex-shrink-0">
                      {creating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-3.5 w-3.5" />} Create
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Create form */}
        {showForm && account && (
          <div className="rounded-xl border border-gray-200 bg-white p-4 space-y-3">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div className="space-y-1.5">
                <label className="block text-xs font-normal text-[#64748B]">Name (snake_case)</label>
                <input className={inputCls} placeholder="platform_alert" value={name} onChange={e => setName(e.target.value)} />
              </div>
              <div className="space-y-1.5">
                <label className="block text-xs font-normal text-[#64748B]">Language</label>
                <input className={inputCls} placeholder="en_US" value={language} onChange={e => setLanguage(e.target.value)} />
              </div>
              <div className="space-y-1.5">
                <label className="block text-xs font-normal text-[#64748B]">Category</label>
                <SelectField className={inputCls} value={category} onChange={e => setCategory(e.target.value)}>
                  {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                </SelectField>
              </div>
            </div>
            <div className="space-y-1.5">
              <label className="block text-xs font-normal text-[#64748B]">Body</label>
              <textarea rows={3} className="w-full px-3 py-2 rounded-lg border border-gray-200 bg-white text-sm text-[#1E293B] focus:outline-none focus:ring-2 focus:ring-[#6366F1]"
                placeholder="🔔 {{1}} — {{2}}" value={body} onChange={e => setBody(e.target.value)} />
              <p className="text-[11px] text-slate-400">Use {'{{1}}'}, {'{{2}}'} … for variables (must be 1,2,3… in order). Meta reviews it — usually a few minutes.</p>
            </div>
            <div className="flex justify-end gap-2">
              <button onClick={() => setShowForm(false)} className="rounded-md border border-gray-200 px-3 py-2 text-sm text-slate-600 hover:bg-slate-50">Cancel</button>
              <button onClick={() => createTemplate({ name, language, category, body })} disabled={creating}
                className="inline-flex items-center gap-1.5 rounded-md bg-[#6366F1] px-3 py-2 text-sm font-medium text-white hover:bg-[#4F46E5] disabled:opacity-50">
                {creating ? <Loader2 className="h-4 w-4 animate-spin" /> : null} Submit
              </button>
            </div>
          </div>
        )}

        {/* List */}
        {account && (
          <div className="rounded-xl border border-gray-100 bg-white overflow-hidden">
            {loading ? (
              <div className="px-5 py-10 text-center text-[#94A3B8]"><Loader2 className="h-5 w-5 animate-spin inline" /></div>
            ) : templates.length === 0 ? (
              <div className="px-5 py-10 text-center text-[#94A3B8]">No templates yet. Create one above.</div>
            ) : templates.map(t => (
              <div key={t.id} className="flex items-center gap-3 px-5 py-3.5 border-b border-gray-50 last:border-0">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-mono text-sm text-[#1E293B]">{t.name}</span>
                    <span className="text-[11px] text-[#94A3B8]">{t.language}</span>
                    <span className="text-[11px] px-2 py-0.5 rounded-full bg-slate-100 text-slate-600">{t.category}</span>
                    <span className={`text-[11px] px-2 py-0.5 rounded-full font-medium ${STATUS_STYLE[t.status] ?? 'bg-slate-100 text-slate-600'}`}>{t.status}</span>
                  </div>
                  {t.rejectionReason && t.status === 'REJECTED' && (
                    <p className="text-[11px] text-red-500 mt-0.5">Rejected: {t.rejectionReason}</p>
                  )}
                </div>
                <button onClick={() => remove(t.id)} className="text-slate-300 hover:text-red-500 flex-shrink-0" aria-label="Delete">
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </SuperAdminLayout>
  );
}
